import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { DrawerService } from '../../services/drawer/drawer.service';
import { modalVars } from '../../../constants/modal-vars';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';

interface IDrawerOptions {
  mobileFullscreen: boolean;
  showBottomCloseBtn: boolean;
}

@Component({
  selector: 'app-drawer',
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
  standalone: false
})
export class DrawerComponent implements AfterViewInit, OnDestroy {
  modalVars = modalVars;
  bottomPosition: number = 0;
  currentScrollPosition: number = 0;

  @Input() id: string;
  @Input() title: string;
  @Input() closeBottomBtnTitle: string; // Supporting Accessibility
  @Input() options: IDrawerOptions = {
    mobileFullscreen: false,
    showBottomCloseBtn: true // Supporting Accessibility
  };

  @ViewChild('dialogContent') dialogContent: ElementRef;

  @Output() onClose: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private readonly drawerService: DrawerService,
    private readonly viewportService: ViewportService,
    private readonly elementRef: ElementRef
  ) {}

  get isMobile() {
    return this.viewportService.isMobileDevice();
  }

  ngAfterViewInit(): void {
    this.onEscapeKeyPress();
  }

  get shouldShowHeaderBorder(): boolean {
    return this.currentScrollPosition > 1;
  }

  get isMobileAndFullscreen(): boolean {
    return this.isMobile && this.options.mobileFullscreen;
  }

  get shouldShowSwiper(): boolean {
    return this.isMobile && !this.isMobileAndFullscreen;
  }

  get bottomPositionStyle(): string {
    return `-${this.bottomPosition}px`;
  }

  get shouldShowBottomCloseButton(): boolean {
    return this.isMobile && this.options.showBottomCloseBtn && !this.options.mobileFullscreen;
  }

  /**
   * Sets scroll position on scroll event
   * @param event
   */
  setScrollPosition(event: any): void {
    this.currentScrollPosition = event.target.scrollTop;
  }

  /**
   * SwipeDown event being triggered on touch devices
   * @param event
   */
  swipeDown(event: any): void {
    let { isFinal, distance } = event;
    const hasValidSettingsToCloseModal: boolean =
      isFinal && !this.options.mobileFullscreen && this.isMobile && distance > 50;
    if (!this.options.mobileFullscreen) {
      this.dialogContent.nativeElement.classList.remove('slide-up-animation');
      this.bottomPosition = distance;
    }
    if (hasValidSettingsToCloseModal) this.close();
  }

  panEnd(): void {
    this.bottomPosition = 0;
    this.resetModalAnimations();
  }

  close(): void {
    this.drawerService.close(this.id);
    this.onClose.emit();
  }

  /**
   * Resets modal animations ->
   * Reason is that when animation is appliet it conflicts with
   * [ngStyle] so it does not allow us to make the modal follow thumb finger
   */
  resetModalAnimations(): void {
    if (this.isMobile) {
      this.dialogContent.nativeElement.classList.add('slide-up-animation');
      return;
    }
    this.dialogContent.nativeElement.classList.add('slide-left-animation');
  }

  /**
   * Closes modal if clicked outside
   * @param event : Event
   */
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    // Check if the click event occurred outside of the modal content
    if (event.target === this.elementRef.nativeElement.firstChild) {
      this.close();
    }
  }

  /**
   * Closes modal on ESC press (Supporting Accessibility)
   * See: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
   */
  onEscapeKeyPress(): void {
    this.elementRef.nativeElement.addEventListener('keydown', (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        const isModalOpen: boolean = modalVars[this.id] === true;
        if (isModalOpen) {
          this.close();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener('keydown', (event: KeyboardEvent): void => {}, true);
  }
}
